/**
 * The lane graph. Owned by `traffic`.
 *
 * Built once from `ctx.world.segments()`. Every road segment becomes two DIRECTED edges, one
 * per travel direction, welded together at shared endpoints into nodes. Agents live in
 * (edge, s, lat) space — distance along the edge and lateral offset from its centre-line —
 * which is what makes lane keeping, car following and overtaking a one-dimensional problem.
 *
 * Right-hand traffic: `right = (-dz, 0, dx)` and every lane offset is POSITIVE, i.e. to the
 * right of the direction of travel. That matches `world.roadAt()`'s convention exactly (on a
 * north-south rue the east half runs −Z, and a car in the east half is to the right of the
 * centre-line of a −Z heading), so an agent placed by this file always agrees with the
 * world's own idea of which way traffic flows.
 *
 * Ruelles are excluded: at 5.6–6.2 m they are too narrow for two-way AI traffic, and they are
 * the player's shortcut. Leaving them empty is a design decision, not an omission.
 */

import { TUNING } from './tuning.js';

const L = TUNING.lanes;

export class LaneGraph {
  constructor(world) {
    this.nodes = [];
    this.edges = [];
    this._nodeKey = new Map();
    this._build(world);
  }

  // ---------------------------------------------------------------- build

  _nodeAt(x, z) {
    const q = L.weld;
    const kx = Math.round(x / q), kz = Math.round(z / q);
    const key = kx * 100003 + kz;
    let idx = this._nodeKey.get(key);
    if (idx === undefined) {
      idx = this.nodes.length;
      this.nodes.push({
        id: idx, x, z,
        out: [],
        arret: true,
        claim: -1,          // agent id allowed through this step
        claimDist: 1e9,
      });
      this._nodeKey.set(key, idx);
    }
    return idx;
  }

  _build(world) {
    const segs = world.segments();
    const skip = L.skipKinds;

    for (const seg of segs) {
      if (skip.indexOf(seg.kind) >= 0) continue;
      const ax = seg.a.x, az = seg.a.z, bx = seg.b.x, bz = seg.b.z;
      const ddx = bx - ax, ddz = bz - az;
      const len = Math.sqrt(ddx * ddx + ddz * ddz);
      if (len < L.minEdgeLen) continue;

      const na = this._nodeAt(ax, az);
      const nb = this._nodeAt(bx, bz);
      if (na === nb) continue;

      const halfW = seg.width * 0.5;
      const lanes = L.lanesPerDir[seg.kind] || 1;

      const e0 = this._pushEdge(na, nb, len, halfW, lanes, seg.kind, seg.id);
      const e1 = this._pushEdge(nb, na, len, halfW, lanes, seg.kind, seg.id);
      this.edges[e0].rev = e1;
      this.edges[e1].rev = e0;

      if (seg.kind === 'artere') {
        this.nodes[na].arret = false;
        this.nodes[nb].arret = false;
      }
    }

    // Outgoing lists live on the node the edge STARTS at.
    for (let i = 0; i < this.edges.length; i++) {
      this.nodes[this.edges[i].from].out.push(i);
    }

    // Precompute, for every edge, the turn weight of each continuation. Doing it here means
    // the per-step routing decision is a weighted pick over a tiny prebuilt array.
    for (let i = 0; i < this.edges.length; i++) {
      const e = this.edges[i];
      const outs = this.nodes[e.to].out;
      const choices = [];
      const weights = [];
      for (let k = 0; k < outs.length; k++) {
        const o = this.edges[outs[k]];
        const dot = e.dx * o.dx + e.dz * o.dz;
        let w;
        if (dot > 0.85) w = 6.0;
        else if (dot < -0.85) w = 0.03;            // U-turn: almost never
        else w = 1.6;
        choices.push(outs[k]);
        weights.push(w);
      }
      e.choices = choices;
      e.weights = weights;
      e.deadEnd = choices.length === 0;
    }

    this.bounds = world.bounds;
  }

  _pushEdge(from, to, len, halfW, lanes, kind, segId) {
    const a = this.nodes[from], b = this.nodes[to];
    const dx = (b.x - a.x) / len, dz = (b.z - a.z) / len;
    const idx = this.edges.length;
    // Drivable half-width: the carriageway MINUS the kerb-side parking strip. Every lane
    // offset is measured inside this, so moving traffic never occupies a parking bay.
    const laneHalf = Math.max(L.minDrivable, halfW - L.parkStrip);
    this.edges.push({
      id: idx, from, to, segId, kind,
      ax: a.x, az: a.z, bx: b.x, bz: b.z,
      dx, dz,
      rx: -dz, rz: dx,          // right-hand normal (see file header)
      len, halfW, laneHalf, lanes,
      rev: -1,
      choices: null, weights: null, deadEnd: false,
      mx: (a.x + b.x) * 0.5, mz: (a.z + b.z) * 0.5,
    });
    return idx;
  }

  // ---------------------------------------------------------------- queries

  get edgeCount() { return this.edges.length; }
  get nodeCount() { return this.nodes.length; }

  /**
   * Centre of lane `i` (0 = nearest the centre-line), as a positive right-hand offset.
   * Measured inside the DRIVABLE half-width, not the full carriageway — see `parkStrip`.
   */
  laneOffset(edge, i) {
    const n = edge.lanes;
    const k = i < 0 ? 0 : i >= n ? n - 1 : i;
    return edge.laneHalf * (k + 0.5) / n;
  }

  /** Outermost lateral a moving vehicle of half-width `halfW` may reach. */
  driveLimit(edge, halfW) {
    return Math.max(1.0, edge.laneHalf - halfW * 0.35);
  }

  /**
   * Cyclist lateral: the door zone, just outboard of the outer traffic lane and just inboard
   * of the parked line. Threading past one is meant to be a decision you can feel.
   */
  cyclistOffset(edge) {
    return edge.laneHalf * L.cyclistLatFrac;
  }

  /** Sidewalk centre-line. `side` is +1 (right of travel) or −1. */
  walkOffset(edge, side) {
    return side * (edge.halfW + L.walkInset);
  }

  worldX(edge, s, lat) { return edge.ax + edge.dx * s + edge.rx * lat; }
  worldZ(edge, s, lat) { return edge.az + edge.dz * s + edge.rz * lat; }

  /**
   * Weighted continuation from the end of `edge`. Straight is heavily favoured so traffic
   * flows down a street instead of milling about in the intersection.
   */
  chooseNext(edge, rng) {
    const c = edge.choices;
    if (!c || c.length === 0) return edge.rev;
    if (c.length === 1) return c[0];
    const w = edge.weights;
    let total = 0;
    for (let i = 0; i < w.length; i++) total += w[i];
    let r = rng.next() * total;
    for (let i = 0; i < c.length; i++) {
      r -= w[i];
      if (r <= 0) return c[i];
    }
    return c[c.length - 1];
  }

  /**
   * Nearest directed edge to a world point, optionally biased toward a heading.
   * Brute force over ~170 edges; only ever called on spawn, never per agent per step.
   */
  nearestEdge(x, z, dirX, dirZ) {
    let best = -1, bestD = Infinity, bestS = 0;
    for (let i = 0; i < this.edges.length; i++) {
      const e = this.edges[i];
      let s = (x - e.ax) * e.dx + (z - e.az) * e.dz;
      if (s < 0) s = 0; else if (s > e.len) s = e.len;
      const px = e.ax + e.dx * s, pz = e.az + e.dz * s;
      const ddx = x - px, ddz = z - pz;
      let d = Math.sqrt(ddx * ddx + ddz * ddz);
      if (dirX !== undefined && dirX !== null) {
        // Penalise edges pointing the wrong way so a spawn point's dir picks the right side.
        const dot = e.dx * dirX + e.dz * dirZ;
        d += (1 - dot) * 6;
      }
      if (d < bestD) { bestD = d; best = i; bestS = s; }
    }
    if (best < 0) return null;
    const e = this.edges[best];
    const lat = (x - e.ax) * e.rx + (z - e.az) * e.rz;
    return { edge: best, s: bestS, lat, dist: bestD };
  }

  /** Reset per-step intersection claims. Called once at the top of each fixed step. */
  clearClaims() {
    const n = this.nodes;
    for (let i = 0; i < n.length; i++) { n[i].claim = -1; n[i].claimDist = 1e9; }
  }
}
